// Artifacts. A boss drops one when defeated; it is not active immediately, it
// must be activated by spending resources (the blueprint's "activate with
// resources" beat). Once activated its effect lasts the run, then resets on
// death like everything else run-scoped.

import type { RunModifiers } from './cards';
import type { ResourceId } from './resources';

export interface ArtifactDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  /** Resources needed to activate the artifact. */
  activationCost: Partial<Record<ResourceId, number>>;
  apply: (m: RunModifiers) => void;
}

export const ARTIFACTS: ArtifactDef[] = [
  {
    id: 'double-harvest',
    name: 'Twin Sickle',
    icon: '🌾',
    desc: 'Double harvest yield and +50% harvest damage.',
    activationCost: { wood: 240, stone: 240 },
    apply: (m) => {
      m.harvestYieldMult *= 2;
      m.harvestDamageMult *= 1.5;
    },
  },
  {
    id: 'aegis',
    name: 'Aegis Core',
    icon: '🛡️',
    desc: 'Double settlement defense.',
    activationCost: { ore: 160, knowledge: 90 },
    apply: (m) => (m.defenseMult *= 2),
  },
  {
    id: 'resource-magnet',
    name: 'Lodestone',
    icon: '🧲',
    desc: '+60% production and +50% auto-collection.',
    activationCost: { wood: 320, stone: 320, ore: 160 },
    apply: (m) => {
      m.productionMult *= 1.6;
      m.autoCollectMult *= 1.5;
    },
  },
  {
    id: 'chronosphere',
    name: 'Chronosphere',
    icon: '⏳',
    desc: '+50% more time between waves.',
    activationCost: { knowledge: 160 },
    apply: (m) => (m.waveIntervalMult *= 1.5),
  },
  {
    id: 'philosophers-stone',
    name: "Philosopher's Stone",
    icon: '💠',
    desc: 'Double knowledge and +30% production.',
    activationCost: { knowledge: 260 },
    apply: (m) => {
      m.knowledgeMult *= 2;
      m.productionMult *= 1.3;
    },
  },
];

export const ARTIFACT_BY_ID: Record<string, ArtifactDef> = {};
for (const a of ARTIFACTS) ARTIFACT_BY_ID[a.id] = a;

// Base costs above are tuned for a mid-run (~age 4) drop, but era gates hand
// artifacts from the age-1 gate on: unscaled, 3 of 5 first-gate drops were
// unaffordable for ages while any deep drop cost pocket change.
const COST_PIVOT_AGE = 4; // the age the base costs were tuned for
const COST_AGE_GROWTH = 1.5; // scale per age away from the pivot
const COST_MULT_MIN = 0.3; // early-gate floor (age-1 drops are usable soon)
const COST_MULT_MAX = 6; // deep-gate cap (late drops stay a real spend, bounded)

/** Activation cost scaled to the CURRENT age (evaluated at activation time, so
 *  hoarding a drop past new gates makes it pricier; activating promptly is the
 *  cheap play). Used by both the activation check and the lab cost display. */
export function activationCostAt(
  def: ArtifactDef,
  age: number,
): Partial<Record<ResourceId, number>> {
  const mult = Math.min(
    COST_MULT_MAX,
    Math.max(COST_MULT_MIN, Math.pow(COST_AGE_GROWTH, age - COST_PIVOT_AGE)),
  );
  const out: Partial<Record<ResourceId, number>> = {};
  for (const [res, amt] of Object.entries(def.activationCost) as [ResourceId, number][]) {
    out[res] = Math.ceil(amt * mult);
  }
  return out;
}
